import { useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import UndoIcon from '@mui/icons-material/Undo';
import { Box } from '@mui/material';
import { ActionButton } from '@core/components';
import { type Order, confirmOrderReprint } from '../../api/order';
import { OrderStatus } from '../enum/orderStatus';
import {
  canCancelOrder,
  canDeleteOrder,
  canEditOrder,
  canUndoCancelOrder,
  canUndoReleaseOrder,
} from '../utils/order';
import CancelOrderDialog from './CancelOrderDialog';
import DeleteOrderDialog from './DeleteOrderDialog';
import MoveToProductionDialog from './MoveToProductionDialog';
import OrderDialog from './OrderDialog';
import ReleaseOrderDialog from './ReleaseOrderDialog';
import UndoCancelDialog from './UndoCancelDialog';
import UndoReleaseDialog from './UndoReleaseDialog';
import { useJobOrderPrint } from './useJobOrderPrint';

interface OrderActionsBarProps {
  order: Order;
  onUpdated: () => void;
  onDeleted?: () => void;
}

const OrderActionsBar = ({
  order,
  onUpdated,
  onDeleted,
}: OrderActionsBarProps) => {
  const [dialogAction, setDialogAction] = useState<
    | 'edit'
    | 'move'
    | 'release'
    | 'undo-release'
    | 'cancel'
    | 'undo-cancel'
    | 'delete'
    | null
  >(null);
  const { isPrintAvailable, printJobOrders, printView, attestationDialog } =
    useJobOrderPrint();

  const handleReprint = () => {
    const itemIds = order.items.map((item) => item.id);
    printJobOrders({
      input: order,
      onConfirm: async () => {
        await confirmOrderReprint(order.id, itemIds);
        onUpdated();
      },
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {canEditOrder(order.status) ? (
          <ActionButton
            variant="outline"
            size="sm"
            icon={<EditIcon fontSize="small" />}
            onClick={() => setDialogAction('edit')}
          >
            Edit
          </ActionButton>
        ) : null}
        {order.status === OrderStatus.ReadyForProduction ? (
          <ActionButton
            variant="primary"
            size="sm"
            icon={<ContentCutIcon fontSize="small" />}
            onClick={() => setDialogAction('move')}
          >
            Move to Production
          </ActionButton>
        ) : null}
        {isPrintAvailable && order.status === OrderStatus.InProduction ? (
          <ActionButton
            variant="outline"
            size="sm"
            icon={<PrintIcon fontSize="small" />}
            onClick={handleReprint}
          >
            Reprint
          </ActionButton>
        ) : null}
        {order.status === OrderStatus.ReadyForRelease ? (
          <ActionButton
            variant="success"
            size="sm"
            icon={<LocalShippingIcon fontSize="small" />}
            onClick={() => setDialogAction('release')}
          >
            Release
          </ActionButton>
        ) : null}
        {canUndoReleaseOrder(order.status) ? (
          <ActionButton
            variant="outline"
            size="sm"
            icon={<UndoIcon fontSize="small" />}
            onClick={() => setDialogAction('undo-release')}
          >
            Undo release
          </ActionButton>
        ) : null}
        {canCancelOrder(order.status) ? (
          <ActionButton
            variant="danger"
            size="sm"
            icon={<CancelIcon fontSize="small" />}
            onClick={() => setDialogAction('cancel')}
          >
            Cancel
          </ActionButton>
        ) : null}
        {canUndoCancelOrder(order.status) ? (
          <ActionButton
            variant="outline"
            size="sm"
            icon={<UndoIcon fontSize="small" />}
            onClick={() => setDialogAction('undo-cancel')}
          >
            Undo cancel
          </ActionButton>
        ) : null}
        {canDeleteOrder(order.status) ? (
          <ActionButton
            variant="danger"
            size="sm"
            icon={<DeleteIcon fontSize="small" />}
            onClick={() => setDialogAction('delete')}
          >
            Delete
          </ActionButton>
        ) : null}
      </Box>
      <OrderDialog
        open={dialogAction === 'edit'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <MoveToProductionDialog
        open={dialogAction === 'move'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <ReleaseOrderDialog
        open={dialogAction === 'release'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <UndoReleaseDialog
        open={dialogAction === 'undo-release'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <CancelOrderDialog
        open={dialogAction === 'cancel'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <UndoCancelDialog
        open={dialogAction === 'undo-cancel'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onUpdated}
      />
      <DeleteOrderDialog
        open={dialogAction === 'delete'}
        onClose={() => setDialogAction(null)}
        order={order}
        onSuccess={onDeleted}
      />
      {printView}
      {attestationDialog}
    </>
  );
};

export default OrderActionsBar;
